import React from 'react';
import { format } from 'date-fns';
import signatureImage from '../images/signature.png';
import { formatIndianCurrency } from '../utils/numberToWords';
import { formatInvoiceNumber } from '../utils/invoiceNumber';

function InvoicePreview({
  client,
  invoiceNumber,
  invoiceDate,
  dueDate,
  placeOfSupply,
  items,
  subtotal,
  cgst,
  sgst,
  roundOff = 0,
  total,
  amountInWords,
  printRef,
  handlePrint,
  config,
  poNumber = '',
  poDate = '',
  showShipTo,
  financialYear,
  referenceQuoteNumber,
  isCreditNote = false,
  additionalInfo,
  invoiceRefNumber,
  originalInvoiceDate,
  hideQuoteNumber = false,
  bankAccount = null, // Bank account stored in invoice
  paymentTerm = '',
  purchaseInvoiceNumber = ''
}) {
  // Use bankAccount from invoice if provided, otherwise use config/client logic
  let bankDetails;
  if (bankAccount && bankAccount.bankName) {
    // Use bank account stored in invoice
    bankDetails = bankAccount;
  } else {
    // Fallback to config/client logic
    const selectedBank = config.bankAccounts?.find(bank => bank.id === client?.bankAccountId);
    bankDetails = selectedBank || config.bankAccounts?.[0] || {
      bankName: 'Bank Name',
      accountName: 'Account Name',
      accountNumber: 'Account Number',
      ifscCode: 'IFSC Code',
      branch: 'Branch'
    };
  }

  // Helper for N/A display
  const displayOrNA = (val) => (val && String(val).trim() !== '' ? val : 'N/A');

  // Helper for safe date formatting
  const formatDate = (dateString) => {
    if (!dateString || dateString === '') return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return format(date, 'dd-MMM-yy');
    } catch (error) {
      console.error('Date formatting error:', error, 'for date:', dateString);
      return 'N/A';
    }
  };

  // Helper for safe invoice number formatting
  const formatInvoiceNumberSafe = (invNo, fy, cfg) => {
    try {
      // If already formatted (contains a year prefix like 2025-26/) just return as-is
      if (typeof invNo === 'string' && /\d{4}-\d{2}\/\d+/.test(invNo)) {
        return invNo;
      }
      if (!fy || typeof fy !== 'string') {
        return invNo || 'N/A';
      }
      return formatInvoiceNumber(invNo, fy, cfg);
    } catch (error) {
      console.error('Invoice number formatting error:', error);
      return invNo || 'N/A';
    }
  };

  return (
    <div className="invoice-preview" ref={printRef} style={{ position: 'relative' }}>
      {/* Watermark logo */}
      <img
        src="/spatium-logo-1.png"
        alt="Watermark"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '60%',
          opacity: 0.07,
          zIndex: 0,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      />
      {/* Main invoice content (zIndex: 1 by default) */}
      <div className="invoice-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', paddingTop: '10px', paddingBottom: '10px' }}>
        <img src="/spatium-logo-1.png" alt="Spatium Logo" style={{ maxWidth: '160px', height: 'auto', flex: '0 0 auto', padding: '10px' }} />
        <div style={{ textAlign: 'right', flex: 1, paddingRight: "10px" }}>
          <h2 className="invoice-title" style={{ marginBottom: 0 }}>{isCreditNote ? 'CREDIT NOTE' : 'TAX INVOICE'}</h2>
          <p style={{ marginTop: -1, marginBottom: 0 }}>Spatium Iconic, 4, 4-1, Bannerghatta Main Road,<br />JP Nagar Phase 3, Bengaluru, Karnataka, 560078</p>
          <p style={{ marginTop: 2, marginBottom: 0 }}><strong>GSTIN:</strong> {config?.gstin || 'GSTIN'}</p>
          {config?.udyamNo && (
            <p style={{ marginTop: 2, marginBottom: 0 }}><strong>UDYAM NO:</strong> {config.udyamNo}</p>
          )}
        </div>
      </div>

      <div className="invoice-details" style={{ display: 'flex', gap: '0px' }}>
        <div style={{ flex: '0 0 54.5%' }}>
          <div className="invoice-info" style={{ borderBottom: '0px' }}>
            <h3>Bill To:</h3>
            <p><strong>{client?.registeredName || client?.name || 'Client Name'}</strong></p>
            <p>{client?.billToAddress || client?.address || 'Client Address'}</p>
            <p>Phone: {client?.phone || 'N/A'}</p>
            <p>GSTIN: {client?.gstin || 'N/A'}</p>
          </div>
          {showShipTo === 'yes' && (
            <div className="invoice-info" style={{ borderBottom: '0px' }} >
              <h3>Ship To:</h3>
              <p><strong>{client?.registeredName || client?.name || 'Client Name'}</strong></p>
              <p>{client?.shipToAddress || client?.address || 'Client Address'}</p>
              <p>Phone: {client?.phone || 'N/A'}</p>
              <p>GSTIN: {client?.gstin || 'N/A'}</p>
            </div>
          )}
        </div>
        <div style={{ flex: '0 0 45.5%' }}>
          <table className="invoice-meta-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <tbody>
              <tr>
                <td style={{ fontWeight: 400, width: '50%', borderRight: '1px solid #333' }}>{isCreditNote ? 'Credit Note No.' : 'Invoice No.'}<br />
                  <span style={{ fontWeight: 700 }}>
                    {isCreditNote ? invoiceNumber : formatInvoiceNumberSafe(invoiceNumber, financialYear, config || {})}
                  </span>
                </td>
                <td style={{ fontWeight: 400 }}>Dated<br /><span style={{ fontWeight: 700 }}>{formatDate(invoiceDate)}</span></td>
              </tr>
              {!isCreditNote && (
                <tr>
                  <td style={{ borderRight: '1px solid #333' }}>Due Date<br />
                    <span style={{ fontWeight: 700 }}>{formatDate(dueDate)}</span>
                  </td>
                  <td>Payment Terms<br /><span style={{ fontWeight: 700 }}>{displayOrNA(paymentTerm)}</span></td>
                </tr>
              )}
              <tr>
                {isCreditNote ? (
                  <>
                    <td style={{ borderRight: '1px solid #333' }}>Invoice Ref. No.<br />
                      <span style={{ fontWeight: 700 }}>
                        {invoiceRefNumber ? invoiceRefNumber : displayOrNA('')}
                      </span>
                    </td>
                    <td>Invoice Date<br /><span style={{ fontWeight: 700 }}>{formatDate(originalInvoiceDate)}</span></td>
                  </>
                ) : (
                  <>
                    <td style={{ borderRight: '1px solid #333' }}>PO Number<br /><span style={{ fontWeight: 700 }}>{displayOrNA(poNumber)}</span></td>
                    <td>PO Date<br /><span style={{ fontWeight: 700 }}>{formatDate(poDate)}</span></td>
                  </>
                )}
              </tr>
              {!isCreditNote && !hideQuoteNumber && referenceQuoteNumber && (
                <tr>
                  <td style={{ borderRight: '1px solid #333', paddingBottom: '3px', borderBottom: '0px' }}>Quote Number<br />
                    <span style={{ fontWeight: 700 }}>{displayOrNA(referenceQuoteNumber)}</span>
                  </td>
                  <td style={{ paddingBottom: '3px', borderBottom: '0px' }}></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <table className="invoice-table">
        <thead>
          <tr>
            <th rowSpan="2" style={{ width: '20px' }}>Sr No</th>
            <th rowSpan="2" style={{ width: '60px' }}>HSN/SAC</th>
            <th className='description-th' rowSpan="2" style={{ width: '495px' }}>Description</th>
            <th rowSpan="2" style={{ width: '103px' }}>Amount</th>
            <th colSpan="2">CGST</th>
            <th colSpan="2">SGST</th>
            <th rowSpan="2" style={{ width: '99px' }}>Total Amount</th>
          </tr>
          <tr>
            <th>Rate</th>
            <th style={{ width: '92px' }}>Amount</th>
            <th>Rate</th>
            <th style={{ width: '92px' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const amount = Number(item.amount || 0);
            const cgstRate = config && config.cgstRate !== undefined ? config.cgstRate : 0;
            const sgstRate = config && config.sgstRate !== undefined ? config.sgstRate : 0;
            const cgst = amount * (cgstRate / 100);
            const sgst = amount * (sgstRate / 100);
            const total = amount + cgst + sgst;
            return (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item.hsn}</td>
                <td>{item.description}</td>
                <td>{formatIndianCurrency(amount)}</td>
                <td>{cgstRate}%</td>
                <td>{formatIndianCurrency(cgst)}</td>
                <td>{sgstRate}%</td>
                <td>{formatIndianCurrency(sgst)}</td>
                <td>{formatIndianCurrency(total)}</td>
              </tr>
            );
          })}

        </tbody>
      </table>

      <div className="invoice-total">
        <table>
          <tbody>
            <tr>
              <td style={{ borderBottom: '0px', borderRight: '0px' }}>Subtotal:</td>
              <td style={{ borderBottom: '0px', width: '83px' }}>{formatIndianCurrency(Number(subtotal))}</td>
            </tr>
            <tr>
              <td style={{ borderBottom: '0px', borderRight: '0px' }}>CGST ({config.cgstRate}%):</td>
              <td style={{ borderBottom: '0px' }}>{formatIndianCurrency(Number(cgst))}</td>
            </tr>
            <tr>
              <td style={{ borderBottom: '0px', borderRight: '0px' }}>SGST ({config.sgstRate}%):</td>
              <td style={{ borderBottom: '0px' }}>{formatIndianCurrency(sgst)}</td>
            </tr>
            <tr>
              <td style={{ borderBottom: '0px', borderRight: '0px' }}>Round Off:</td>
              <td style={{ borderBottom: '0px' }}>{formatIndianCurrency(roundOff)}</td>
            </tr>
            <tr className="total-row">
              <td style={{ borderRight: '0px' }}><strong>Total:</strong></td>
              <td><strong>{formatIndianCurrency(total)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="amount-in-words">
        <strong>Amount in Words:</strong> {amountInWords}
      </div>

      <div className="invoice-footer" style={{ display: 'flex', marginTop: 30 }}>
        <div className="left-col" style={{ flex: '0 0 54.5%', borderLeft: '0px', padding: '8px 12px 0 12px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 180 }}>
          <div>
            {isCreditNote ? (
              <div>
                <h3 style={{ marginTop: 0, marginBottom: 8, color: '#333' }}>Additional information</h3>
                <p style={{ color: '#555', margin: 0, lineHeight: 1.5 }}>{additionalInfo}</p>
              </div>
            ) : (
              <div>
                <h4 style={{ marginTop: 0, color: '#333' }}>Bank Details</h4>
                <p><strong>Bank Name:</strong> {bankDetails.bankName}</p>
                <p><strong>Account Name:</strong> {bankDetails.accountName}</p>
                <p><strong>Account No.:</strong> {bankDetails.accountNumber}</p>
                <p><strong>IFSC Code:</strong> {bankDetails.ifscCode}</p>
                <p><strong>Branch:</strong> {bankDetails.branch}</p>
              </div>
            )}
          </div>
          {/* Made with block */}
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0, marginBottom: '10px' }}>
            <span style={{ fontSize: 12, color: '#666' }}>Made with</span>
            <img src="/spatium-logo.png" alt="Ledgerly Logo" style={{ width: 80, height: 25, objectFit: 'contain' }} />
            <span style={{ fontSize: 10, color: '#666' }}>A product of Spatium Offices</span>
          </div>
        </div>
        <div className="bank-details" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'flex-end', minHeight: 160 }}>
          <h3 style={{ marginBottom: 6, color: '#333' }}>Authorized Signatory</h3>
          <img src={signatureImage} alt="Signature" style={{ maxWidth: '120px', height: 'auto', margin: '16px 0 0 0' }} />
          <div style={{ borderTop: '1.5px solid #bbb', width: '80%', marginTop: '32px', marginBottom: '8px' }}></div>
          <span style={{ fontSize: 13, color: '#888' }}>(Signature)</span>
        </div>
      </div>

      {/* Print button moved to modal title bar; keep this empty for spacing if needed */}
    </div>
  );
}

export default InvoicePreview; 